import { type Params } from 'react-router-dom'
import { type GetOrder } from '#app/models/ecommerce-providers/sylius/shop/fetch/order/get-order.ts'
import { type Locale } from '#app/models/locale.ts'

/* -------------------------------------------------------------------------------------------------
 * App
 * -----------------------------------------------------------------------------------------------*/

type App = {
	displaySidebar: boolean
	displayModal: boolean
	sidebarView: 'CART_VIEW' | 'MENU_VIEW'
	modalView:
		| 'REGISTRATION_VIEW'
		| 'LOGIN_VIEW'
		| 'RESET_PASSWORD_VIEW'
		| 'ADDRESS_VIEW'
		| 'PAYMENT_METHOD_VIEW'
	options: {
		redirect?: string | null
		preventScrollReset?: boolean
		noJs?: string | null
		successCallback?: () => void | null
		errorCallback?: () => void | null
		data?: {
			[key: string]: string
		}
	}
}

/* -------------------------------------------------------------------------------------------------
 * Context
 * -----------------------------------------------------------------------------------------------*/

type Context = {
	app: App
	cart: GetOrder
	locale: Locale
	loggedIn: boolean
	setApp: React.Dispatch<React.SetStateAction<App>>
}

/* -------------------------------------------------------------------------------------------------
 * FormEnum
 * -----------------------------------------------------------------------------------------------*/

type FormEnum<T = { id: string }> = {
	subaction: string
} & (
	| {
			generateId: (props: T) => string
			id?: never
	  }
	| {
			id: string
			generateId?: never
	  }
) &
	(
		| {
				generateAction: (params: Params) => string
				action?: never
		  }
		| {
				action: string
				generateAction?: never
		  }
	) &
	(
		| {
				generateFetcherKey: (props: T) => string
				fetcherKey?: never
		  }
		| {
				fetcherKey: string
				generateFetcherKey?: never
		  }
	) &
	(
		| {
				generateRedirect: (params: Params) => string
				redirect?: never
		  }
		| {
				redirect: string
				generateRedirect?: never
		  }
	)

/* -------------------------------------------------------------------------------------------------
 * App view
 * -----------------------------------------------------------------------------------------------*/

enum APP_VIEW {
	cartView = 'CART_VIEW',
	menuView = 'MENU_VIEW',
	registrationView = 'REGISTRATION_VIEW',
	loginView = 'LOGIN_VIEW',
	resetPasswordView = 'RESET_PASSWORD_VIEW',
	addressView = 'ADDRESS_VIEW',
	paymentMethodView = 'PAYMENT_METHOD_VIEW',
}

/* -------------------------------------------------------------------------------------------------
 * I18next
 * -----------------------------------------------------------------------------------------------*/

enum I18NEXT {
	routeRoot = 'route-_root',
	routeAuthLogin = 'route-_auth-login',
	routeAuthRegistration = 'route-_auth-registration',
	routeAuthResetPassword = 'route-_auth-reset-password',
	routeNewsletter = 'route-newsletter',
	routeCart = 'route-cart',
	uiInput = 'ui-input',
	uiPayment = 'ui-payment',
	uiI18n = 'ui-i18n',
	uiCloseButton = 'ui-close-button',
	uiRating = 'ui-rating',
	uiProductGridItem = 'ui-product-grid-item',
	uiFormDefaults = 'ui-form-defaults',
}

/* -------------------------------------------------------------------------------------------------
 * Route loader
 * -----------------------------------------------------------------------------------------------*/

enum ROUTE_LOADER {
	root = 'root',
}

/* -------------------------------------------------------------------------------------------------
 * Inputs
 * -----------------------------------------------------------------------------------------------*/

enum INPUTS {
	subaction = 'subaction',
	redirect = 'redirect',
	noJs = 'no-js',
}

/* -------------------------------------------------------------------------------------------------
 * Search params
 * -----------------------------------------------------------------------------------------------*/

enum SEARCH_PARAMS {
	redirect = 'redirect',
	page = 'page',
	itemsPerPage = 'itemsPerPage',
}

/* -----------------------------------------------------------------------------------------------*/

export {
	type App,
	type Context,
	type FormEnum,
	APP_VIEW as ROOT_APP_VIEW,
	I18NEXT as ROOT_I18NEXT,
	ROUTE_LOADER as ROOT_ROUTE_LOADER,
	INPUTS as ROOT_INPUTS,
	SEARCH_PARAMS as ROOT_SEARCH_PARAMS,
}
